/**
 * The Edit-category tools: Add data, Edit, Swipe.
 *
 * Editing is the one tool here that can genuinely fail for reasons outside the
 * app — a service simply may not allow writes. That is reported from the
 * service's own `capabilities` string rather than guessed, so the panel never
 * offers a Save that the server will reject.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  Circle,
  Hand,
  Loader2,
  MapPin,
  Minus,
  Move,
  Pencil,
  Redo2,
  Search,
  Spline,
  Square,
  Trash2,
  Triangle,
  Undo2,
  Upload,
  type LucideIcon,
} from 'lucide-react';
import ToolPanelHeader, { ToolActions } from '../ToolPanel';
import { cfg, scopedLayers, type ToolContext } from './types';
import { detectLayerSource, getLayerGeometryType } from '../../../layers/capabilities';
import {
  EmptyHint,
  Field,
  LayerSelect,
  Notice,
  ResultHeader,
  ResultRow,
  ScrollList,
  btnClass,
  inputClass,
} from './shared';
import { arcgisServiceUrlFor, getArcgisBaseUrl, withArcgisToken } from '../../../utils/arcgis';
import { computeBounds } from '../../../layers/localFile';
import { pointInPolygon, positionsOf } from '../../../utils/spatialSelect';
import { layerLibraryService } from '../../../services/layerLibraryService';
import { reshapeGeometryWithSketch } from '../../../utils/reshapeGeometry';
import {
  LOCAL_ID_FIELD,
  coerceLocalValue,
  readEditInfo,
  type EditableField,
  type ServiceEditInfo,
} from '../../../layers/featureEditing';

/* ────────────────────────────── Add data ────────────────────────────────── */

const FORMAT_LABELS: Record<string, string> = {
  geojson: 'GeoJSON',
  kml: 'KML / KMZ',
  shapefile: 'Shapefile (.zip)',
  csv: 'CSV / TSV',
};

export function AddDataPanel({ ctx }: { ctx: ToolContext }) {
  const formats = cfg<string[]>(ctx.config, 'formats', ['geojson', 'kml', 'shapefile', 'csv']);
  const maxSizeMb = cfg<number>(ctx.config, 'maxSizeMb', 25);

  return (
    <>
      <ToolPanelHeader
        title="Add data"
        description="Bring a service layer or a file into this map."
        onClose={ctx.onClose}
      />

      {ctx.onAddData ? (
        <ToolActions>
          <button
            type="button"
            onClick={ctx.onAddData}
            className={`${btnClass} gl-btn--primary`}
          >
            <Upload className="h-3.5 w-3.5" />
            Add layers
          </button>
        </ToolActions>
      ) : (
        <Notice tone="warn">
          Adding data is turned off in the published app. Open this map in the builder to add layers.
        </Notice>
      )}

      <div className="space-y-1">
        <ResultHeader label="Accepted files" count={formats.length} />
        {formats.map((format) => (
          <ResultRow key={format} title={FORMAT_LABELS[format] ?? format} />
        ))}
      </div>

      <p className="text-[10.5px] leading-snug text-slate-400">
        Up to {maxSizeMb} MB per file. Files are parsed in the browser and travel with the map —
        nothing is uploaded to a server.
      </p>
    </>
  );
}

/* ──────────────────────────────── Edit ──────────────────────────────────── */

/**
 * A new feature's sentinel `activeId` — never a real OID or index, so it
 * cannot collide with one and the save path knows to create rather than
 * update.
 */
const NEW_FEATURE_ID = '__new__';

/**
 * Metres per screen pixel at a zoom and latitude — the standard Web Mercator
 * relationship, used so "close enough to click" means the same thing at every
 * zoom level without needing the Leaflet map instance itself: this panel is
 * not inside the map's React tree, only `ctx.pointer` and `ctx.mapZoom` are.
 */
function metresPerPixel(zoom: number, latDeg: number): number {
  return (156543.03392 * Math.cos((latDeg * Math.PI) / 180)) / 2 ** zoom;
}

const EARTH_RADIUS_M = 6378137;

/** A lon/lat turned into flat metres around an origin — accurate at the short
 *  ranges a single click's hit-test tolerance ever covers. */
function localMetres(position: [number, number], origin: [number, number]): [number, number] {
  const metresPerDegLat = EARTH_RADIUS_M * (Math.PI / 180);
  const metresPerDegLng = metresPerDegLat * Math.cos((origin[1] * Math.PI) / 180);
  return [(position[0] - origin[0]) * metresPerDegLng, (position[1] - origin[1]) * metresPerDegLat];
}

function distanceToSegmentMetres(point: [number, number], a: [number, number], b: [number, number]): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  if (dx === 0 && dy === 0) return Math.hypot(point[0] - a[0], point[1] - a[1]);
  const t = Math.max(0, Math.min(1, ((point[0] - a[0]) * dx + (point[1] - a[1]) * dy) / (dx * dx + dy * dy)));
  return Math.hypot(point[0] - (a[0] + t * dx), point[1] - (a[1] + t * dy));
}

/** Every straight run in a geometry's coordinates, whatever the nesting —
 *  a line's segments, or a polygon ring's edges (rings nest exactly like a
 *  line, so the same walk finds both). */
function* segmentsOf(geometry: GeoJSON.Geometry): Generator<[number[], number[]]> {
  const walkLine = function* (line: any): Generator<[number[], number[]]> {
    if (!Array.isArray(line) || line.length < 2) return;
    for (let index = 1; index < line.length; index += 1) {
      if (typeof line[index - 1]?.[0] === 'number' && typeof line[index]?.[0] === 'number') {
        yield [line[index - 1], line[index]];
      }
    }
  };
  const walk = function* (value: any): Generator<[number[], number[]]> {
    if (!Array.isArray(value)) return;
    if (Array.isArray(value[0]) && typeof value[0][0] === 'number') {
      yield* walkLine(value);
      return;
    }
    for (const part of value) yield* walk(part);
  };
  yield* walk((geometry as any).coordinates);
}

/** How far a click sits from a feature's geometry, in metres. A polygon that
 *  contains the click is distance 0 — the same "inside counts as a hit" rule
 *  the normal identify pop-up uses for local layers. */
function distanceToFeatureMetres(click: { lat: number; lng: number }, geometry: GeoJSON.Geometry | undefined): number {
  if (!geometry) return Infinity;
  const origin: [number, number] = [click.lng, click.lat];
  const toLocal = (position: number[]) => localMetres([position[0], position[1]], origin);

  if (geometry.type === 'Point' || geometry.type === 'MultiPoint') {
    let nearest = Infinity;
    for (const position of positionsOf(geometry)) nearest = Math.min(nearest, Math.hypot(...toLocal(position)));
    return nearest;
  }

  if (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') {
    const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
    for (const rings of polygons as number[][][][]) {
      const localRings = rings.map((ring) => ring.map((position) => toLocal(position)));
      if (pointInPolygon([0, 0], localRings)) return 0;
    }
  }

  let nearest = Infinity;
  for (const [a, b] of segmentsOf(geometry)) {
    nearest = Math.min(nearest, distanceToSegmentMetres([0, 0], toLocal(a), toLocal(b)));
  }
  return nearest;
}

/** The nearest feature to a click, within tolerance — null when nothing is close. */
function nearestEditableFeature(
  click: { lat: number; lng: number },
  features: GeoJSON.Feature[],
  toleranceM: number,
): { feature: GeoJSON.Feature; index: number } | null {
  let best: { feature: GeoJSON.Feature; index: number; distance: number } | null = null;
  features.forEach((feature, index) => {
    const distance = distanceToFeatureMetres(click, feature.geometry);
    if (distance <= toleranceM && (!best || distance < best.distance)) {
      best = { feature, index, distance };
    }
  });
  return best;
}

/** GeoJSON geometry, as the shape ArcGIS's REST `applyEdits` expects it. */
function toEsriGeometry(geometry: GeoJSON.Geometry): Record<string, unknown> | null {
  const spatialReference = { wkid: 4326 };
  switch (geometry.type) {
    case 'Point': {
      const [x, y] = geometry.coordinates;
      return { x, y, spatialReference };
    }
    case 'LineString':
      return { paths: [geometry.coordinates], spatialReference };
    case 'MultiLineString':
      return { paths: geometry.coordinates, spatialReference };
    case 'Polygon':
      return { rings: geometry.coordinates, spatialReference };
    case 'MultiPolygon':
      return { rings: geometry.coordinates.flat(), spatialReference };
    default:
      return null;
  }
}

/** Which new-feature shapes make sense for a layer's own geometry — Esri's
 *  own editor offers only the template matching the layer's type, and an
 *  "unknown" layer (a file with no declared type) is left permissive. */
/** Every shape the Edit tool's sketch controller (shared with Draw/Measure/
 *  Select — see ToolContext.onStartFeatureSketch) can produce. */
type CreatableShape =
  | 'point'
  | 'line'
  | 'polygon'
  | 'circle'
  | 'rectangle'
  | 'freehand-polygon'
  | 'freehand-line';

function creatableShapesFor(layer: any): ('point' | 'line' | 'polygon')[] {
  const type = getLayerGeometryType(layer);
  if (type === 'point') return ['point'];
  if (type === 'line') return ['line'];
  if (type === 'polygon') return ['polygon'];
  return ['point', 'line', 'polygon'];
}

/** Quick-start construction tools, alongside the plain point/line/polygon
 *  sketch above — circle and rectangle finish in two clicks with no
 *  vertex-by-vertex placement, and freehand/curve trace continuously while
 *  the mouse is held down rather than click-per-vertex. All four still end
 *  up as the same plain GeoJSON a normal sketch would (a circle becomes a
 *  72-sided polygon ring, a freehand trace an ordinary LineString/Polygon),
 *  so nothing downstream needs to know a shortcut was used to draw them. */
function extraShapeToolsFor(
  layer: any,
): ('circle' | 'rectangle' | 'freehand-polygon' | 'freehand-line')[] {
  const type = getLayerGeometryType(layer);
  if (type === 'polygon') return ['circle', 'rectangle', 'freehand-polygon'];
  if (type === 'line') return ['freehand-line'];
  if (type === 'point') return [];
  return ['circle', 'rectangle', 'freehand-polygon', 'freehand-line'];
}

const CREATE_SHAPE_ICONS: Record<'point' | 'line' | 'polygon', LucideIcon> = {
  point: MapPin,
  line: Minus,
  polygon: Triangle,
};

const EXTRA_SHAPE_TOOLS: Record<
  'circle' | 'rectangle' | 'freehand-polygon' | 'freehand-line',
  { icon: LucideIcon; label: string }
> = {
  circle: { icon: Circle, label: 'Circle' },
  rectangle: { icon: Square, label: 'Rectangle' },
  'freehand-polygon': { icon: Pencil, label: 'Freehand' },
  'freehand-line': { icon: Spline, label: 'Curve' },
};

export function EditPanel({ ctx }: { ctx: ToolContext }) {
  const allowed = scopedLayers(ctx.config, ctx.layers);
  const allowedFieldNames = cfg<string[]>(ctx.config, 'fields', []);
  const confirmBeforeSave = cfg<boolean>(ctx.config, 'confirmBeforeSave', true);

  const [layerId, setLayerId] = useState(allowed[0]?.id ?? '');
  const [info, setInfo] = useState<ServiceEditInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<GeoJSON.Feature[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  /** The open record's geometry — an existing feature's own, or a sketch just
   *  finished for a new one. The one thing both Save paths write. */
  const [workingGeometry, setWorkingGeometry] = useState<GeoJSON.Geometry | null>(null);
  /** Every geometry the open record has gone through (sketch result,
   *  reshape, move), oldest first — Undo/Redo just walk `historyIndex`
   *  back and forth through it. Reset to a single entry whenever a
   *  DIFFERENT record is opened; entries beyond `historyIndex` are dropped
   *  the next time something new is pushed, the usual undo/redo contract
   *  (you can't redo past a fresh edit). */
  const [geometryHistory, setGeometryHistory] = useState<GeoJSON.Geometry[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [creatingShape, setCreatingShape] = useState<CreatableShape | null>(null);
  const [reshaping, setReshaping] = useState(false);
  const [moving, setMoving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [activeSubTab, setActiveSubTab] = useState<'geometry' | 'attribute'>('geometry');
  const [attributeSearch, setAttributeSearch] = useState('');
  /** The open record's attributes/geometry as last loaded or saved — Save
   *  stays disabled until `draft`/`workingGeometry` differ from these, so it
   *  never offers to write back a no-op edit. Refs, not state: updating them
   *  must never itself trigger a re-render (only `draft`/`workingGeometry`
   *  changing should), and nothing here is ever read during render except
   *  through the `hasChanges` comparison below. */
  const originalDraftRef = useRef<Record<string, unknown>>({});
  const originalGeometryRef = useRef<GeoJSON.Geometry | null>(null);

  const layer = allowed.find((entry) => entry.id === layerId);

  /* `ctx.canManageLayers` guards the SHARED map/TOC in a read-only viewer —
     rename, symbology, reorder, remove. A genuinely SHARED layer's data is
     not part of that: it lives in `shared_layers` in Postgres, and the whole
     point of sharing it (see the backend's `create_shared_layer_feature`
     docstring) is that everyone it's shared with can add to and edit it, not
     just look at a read-only copy. Without this override, every viewer who
     isn't the map's own builder would see this tool as permanently disabled,
     even on data that was deliberately made write-through editable for them.

     This is a LAYER/route check — "does this layer allow writes at all" —
     independent of the two below, which are PERMISSION checks — "may THIS
     user write". Both have to hold for anything to actually be editable;
     `canEditLayer` alone used to be the only gate, which is why (before
     `data:edit`/`data:edit-attributes` existed) anyone who could reach the
     Builder could edit any layer's data regardless of role. */
  const canEditLayer = ctx.canManageLayers || Boolean(layer?.sharedLayerId);
  /** Full edit: create, reshape/move geometry, delete. */
  const canEditGeometry = canEditLayer && Boolean(ctx.canEditGeometry);
  /** Either permission — `canEditGeometry` is a strict superset, so this is
   *  true whenever attribute values may be changed at all, full-edit or
   *  attributes-only alike. */
  const canEditAttributes = canEditLayer && (Boolean(ctx.canEditGeometry) || Boolean(ctx.canEditAttributes));

  /** Ends a create-sketch or a reshape armed on the map — used whenever the
   *  panel steps away from what it was doing, so the map never keeps
   *  listening for a gesture nothing is left waiting on. */
  const clearMapModes = () => {
    setCreatingShape(null);
    setReshaping(false);
    setMoving(false);
    ctx.onCancelFeatureSketch?.();
    ctx.onDraftFeature?.(null);
    ctx.onMoveFeature?.(null);
  };

  useEffect(() => {
    if (!layer) {
      setInfo(null);
      return undefined;
    }
    let live = true;
    setLoading(true);
    setRecords([]);
    setActiveId(null);
    setWorkingGeometry(null);
    setMessage(null);
    clearMapModes();

    const load = async () => {
      const read = await readEditInfo(layer, ctx.arcgisUrl ?? '');
      if (!live) return;
      setInfo(read);
      if (read.editable) {
        if (layer.localData) {
          setRecords(layer.localData.features ?? []);
        } else {
          const recordsServiceUrl = arcgisServiceUrlFor(layer, { arcgisUrl: ctx.arcgisUrl });
          const baseUrl = getArcgisBaseUrl(recordsServiceUrl);
          const params = new URLSearchParams({
            where: '1=1',
            outFields: '*',
            outSR: '4326',
            f: 'geojson',
            resultRecordCount: '200',
          });
          try {
            const response = await fetch(
              withArcgisToken(`${baseUrl}/${layer.arcgisLayerId}/query?${params.toString()}`, recordsServiceUrl),
            );
            const data = response.ok ? await response.json() : null;
            if (live) setRecords(data?.features ?? []);
          } catch {
            /* the panel still works; the list is simply empty */
          }
        }
      }
      if (live) setLoading(false);
    };
    void load();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerId, ctx.arcgisUrl]);

  const shownFields = useMemo(() => {
    if (!info) return [];
    if (allowedFieldNames.length === 0) return info.fields;
    return info.fields.filter((field) => allowedFieldNames.includes(field.name));
  }, [info, allowedFieldNames]);

  /* Leaving the tab must not leave the map waiting for a click that will
     never come, or holding drag handles for a form that is no longer open. */
  useEffect(() => () => clearMapModes(), []); // eslint-disable-line react-hooks/exhaustive-deps

  /* A sketch just finished — either a brand new feature, or (while
     `reshaping`) the replacement path for the Reshape flow, which both arm
     through the same `onStartFeatureSketch`/`sketchedFeature` channel.
     Guarded by a token (not a boolean) for the same reason `measuredFeature`
     is: the same shape finished twice in a row must still count twice, and
     a value that only ever CHANGES cannot tell two identical results apart.
     Seeded from the current token on mount so a sketch finished in an
     EARLIER instance of this panel (a different layer, before it was closed
     and reopened) is never replayed as if it were new. */
  const handledSketchToken = useRef(ctx.sketchedFeature?.token ?? 0);
  useEffect(() => {
    // Gated on the token alone, not on the local `creatingShape`/`reshaping`
    // flags: those flags and the map's own armed state are two different
    // pieces of state updated by two different components, and a click
    // finishing a sketch updates both `ctx.pointer` and `ctx.sketchedFeature`
    // in the very same batch — trusting a local flag here would race the
    // very update it is meant to interpret. The token is the only thing
    // that has to be right, and it already refuses to replay the same
    // result twice. `reshaping`/`workingGeometry` are still read below, but
    // only for WHICH BRANCH to take, each already fresh from this same
    // render's props/state by the time a sketch can possibly finish.
    if (!ctx.sketchedFeature) return;
    if (ctx.sketchedFeature.token === handledSketchToken.current) return;
    handledSketchToken.current = ctx.sketchedFeature.token;

    if (reshaping && workingGeometry) {
      const sketch = ctx.sketchedFeature.feature.geometry;
      const sketchCoords = sketch.type === 'LineString' ? (sketch.coordinates as [number, number][]) : null;
      const reshaped = sketchCoords ? reshapeGeometryWithSketch(workingGeometry, sketchCoords) : null;
      if (reshaped) {
        setWorkingGeometry(reshaped);
        pushGeometryHistory(reshaped);
      } else {
        setMessage({ tone: 'warn', text: 'Could not reshape with that sketch — try drawing across the feature again.' });
      }
      setReshaping(false);
      return;
    }

    setCreatingShape(null);
    setWorkingGeometry(ctx.sketchedFeature.feature.geometry);
    setGeometryHistory([ctx.sketchedFeature.feature.geometry]);
    setHistoryIndex(0);
    setDraft({});
    setActiveId(NEW_FEATURE_ID);
    setMessage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.sketchedFeature]);

  /* The whole feature was dragged — keep the open record's geometry in step. */
  const handledMoveToken = useRef(ctx.movedFeature?.token ?? 0);
  useEffect(() => {
    if (!moving || !ctx.movedFeature) return;
    if (ctx.movedFeature.token === handledMoveToken.current) return;
    handledMoveToken.current = ctx.movedFeature.token;
    setWorkingGeometry(ctx.movedFeature.geometry);
    pushGeometryHistory(ctx.movedFeature.geometry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.movedFeature]);

  /* Shows the open record's current geometry on the map as a highlighted,
     dashed preview — without this, a brand-new sketch, the feature being
     reshaped, a freshly spliced reshape result, or a just-finished move
     would have no on-map representation at all until Save. Kept in sync
     for as long as a record with a geometry is open, Undo/Redo included. */
  useEffect(() => {
    ctx.onDraftFeature?.(activeId && workingGeometry ? workingGeometry : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, workingGeometry]);

  /* Selecting a feature by clicking it on the map — Edit owns clicks while it
     is the open tab (see CLICK_OWNING_TOOL_IDS), so a plain click means "open
     this one", the same gesture the Coordinates tool reads as "read this
     point". Skipped while sketching or reshaping, where a click is placing a
     vertex or dragging a handle, not picking a different feature. */
  const lastPointerHandled = useRef('');
  useEffect(() => {
    if (creatingShape || reshaping || activeId) return;
    if (!info?.editable || !ctx.pointer) return;
    const signature = `${ctx.pointer.lat},${ctx.pointer.lng}`;
    if (signature === lastPointerHandled.current) return;
    lastPointerHandled.current = signature;

    const toleranceM = metresPerPixel(ctx.mapZoom, ctx.pointer.lat) * 12;
    const hit = nearestEditableFeature(ctx.pointer, records, toleranceM);
    if (hit) openRecord(hit.feature, hit.index);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.pointer]);

  /** A row's own display label — the OID for a service layer, the position for a file. */
  const rowLabel = (index: number, properties: Record<string, unknown>) =>
    info?.objectIdField === LOCAL_ID_FIELD
      ? `Row ${index + 1}`
      : `${info?.objectIdField} ${properties[info?.objectIdField ?? '']}`;

  const openRecord = (feature: GeoJSON.Feature, index: number) => {
    const properties = (feature.properties ?? {}) as Record<string, unknown>;
    const id =
      info?.objectIdField === LOCAL_ID_FIELD
        ? String(index)
        : String(properties[info?.objectIdField ?? 'OBJECTID'] ?? feature.id ?? '');
    setActiveId(id);
    setDraft({ ...properties });
    setWorkingGeometry(feature.geometry ?? null);
    originalDraftRef.current = { ...properties };
    originalGeometryRef.current = feature.geometry ?? null;
    setGeometryHistory(feature.geometry ? [feature.geometry] : []);
    setHistoryIndex(feature.geometry ? 0 : -1);
    setReshaping(false);
    setMoving(false);
    setMessage(null);
  };

  /** Whether `draft`/`workingGeometry` differ from what was last loaded or
   *  saved — a new, not-yet-saved sketch always counts (there is nothing to
   *  compare it against yet; its geometry alone is the reason it exists). */
  const hasChanges =
    activeId === NEW_FEATURE_ID
      ? Boolean(workingGeometry)
      : JSON.stringify(draft) !== JSON.stringify(originalDraftRef.current) ||
        JSON.stringify(workingGeometry) !== JSON.stringify(originalGeometryRef.current);

  /** Records a geometry change (reshape, move) as a new, redo-clearing
   *  history entry — called right alongside every `setWorkingGeometry` that
   *  represents a genuine edit, never for just opening/closing a record. */
  const pushGeometryHistory = (geometry: GeoJSON.Geometry) => {
    setGeometryHistory((current) => [...current.slice(0, historyIndex + 1), geometry]);
    setHistoryIndex(historyIndex + 1);
  };

  const canUndoGeometry = historyIndex > 0;
  const canRedoGeometry = historyIndex < geometryHistory.length - 1;

  const undoGeometry = () => {
    if (!canUndoGeometry) return;
    const newIndex = historyIndex - 1;
    setHistoryIndex(newIndex);
    setWorkingGeometry(geometryHistory[newIndex]);
  };

  const redoGeometry = () => {
    if (!canRedoGeometry) return;
    const newIndex = historyIndex + 1;
    setHistoryIndex(newIndex);
    setWorkingGeometry(geometryHistory[newIndex]);
  };

  /** Leaves whatever record is open — the list's own "Back", and Cancel on
   *  the attribute form. Takes any drag handles on the map with it. */
  const closeActive = () => {
    setActiveId(null);
    setDraft({});
    setWorkingGeometry(null);
    clearMapModes();
    setMessage(null);
  };

  const startCreating = (shape: CreatableShape) => {
    setCreatingShape(shape);
    ctx.onStartFeatureSketch?.(shape);
  };

  const cancelCreating = () => {
    setCreatingShape(null);
    ctx.onCancelFeatureSketch?.();
  };

  /**
   * Esri's own "Reshape Feature" tool: sketch a brand new path across the
   * part of the line/polygon being changed, and the span between the
   * sketch's two ends is replaced by it (see utils/reshapeGeometry.ts).
   * Arms the SAME line-sketch `onStartFeatureSketch` already uses for New
   * feature — the sketch-finished effect above tells the two apart via
   * `reshaping`.
   */
  const toggleReshape = () => {
    if (reshaping) {
      setReshaping(false);
      ctx.onCancelFeatureSketch?.();
    } else if (workingGeometry) {
      setMoving(false);
      ctx.onMoveFeature?.(null);
      setReshaping(true);
      ctx.onStartFeatureSketch?.('line');
    }
  };

  const toggleMove = () => {
    if (moving) {
      setMoving(false);
      ctx.onMoveFeature?.(null);
    } else if (workingGeometry) {
      if (reshaping) {
        setReshaping(false);
        ctx.onCancelFeatureSketch?.();
      }
      setMoving(true);
      ctx.onMoveFeature?.(workingGeometry);
    }
  };

  const save = async () => {
    if (!layer || !info || !activeId) return;
    const isNew = activeId === NEW_FEATURE_ID;
    // Belt and suspenders: the "New feature" buttons and the Reshape button
    // are already hidden without `canEditGeometry` (see the JSX below), so
    // this should be unreachable — but `save()` itself is where geometry
    // actually leaves the browser, so it checks again rather than trusting
    // that nothing upstream ever changes.
    if (isNew && !canEditGeometry) return;
    if (isNew && !workingGeometry) {
      setMessage({ tone: 'warn', text: 'Finish sketching the shape before saving it.' });
      return;
    }
    if (confirmBeforeSave && !window.confirm(isNew ? 'Create this feature?' : 'Save these changes?')) return;

    if (layer.localData) {
      /* No service to write to — this is the module's own copy of the data
         (or, for a SHARED layer, a live read of `shared_layers` — see
         `Layer.sharedLayerId`'s own doc comment), so "saving" means updating
         that copy through the same onLayerChange path symbology and labeling
         already go through: persisted in the Builder, session-only in the
         Viewer, UNLESS this layer is shared, in which case it is written
         through to the server immediately below instead — every other
         application holding the same layer needs to see it too, which only
         the server can make true. */
      const features = layer.localData.features ?? [];

      if (isNew) {
        const properties: Record<string, unknown> = {};
        for (const field of shownFields) properties[field.name] = coerceLocalValue(draft[field.name], field.type);

        if (layer.sharedLayerId) {
          setSaving(true);
          setMessage(null);
          try {
            const updated = await layerLibraryService.createSharedLayerFeature(
              ctx.module.id,
              layer.id,
              properties,
              workingGeometry,
            );
            const updatedFeatures = updated.localData?.features ?? [];
            ctx.onLayerChange?.(layer.id, {
              localData: updated.localData,
              bounds: updated.localData ? computeBounds(updated.localData) : undefined,
            });
            setRecords(updatedFeatures);
            setMessage({ tone: 'info', text: 'Feature created — visible to everyone this layer is shared with.' });
            closeActive();
          } catch (error) {
            setMessage({
              tone: 'warn',
              text: error instanceof Error ? error.message : 'Could not create that feature.',
            });
          } finally {
            setSaving(false);
          }
          return;
        }

        const newFeature: GeoJSON.Feature = { type: 'Feature', properties, geometry: workingGeometry! };
        const updatedFeatures = [...features, newFeature];
        const updatedData = { ...layer.localData, features: updatedFeatures };
        ctx.onLayerChange?.(layer.id, { localData: updatedData, bounds: computeBounds(updatedData) });
        setRecords(updatedFeatures);
        setMessage({ tone: 'info', text: 'Feature created.' });
        closeActive();
        return;
      }

      const index = Number(activeId);
      const original = features[index];
      if (!original) {
        setMessage({ tone: 'warn', text: 'That feature is no longer in this layer.' });
        return;
      }
      const updatedProperties: Record<string, unknown> = { ...(original.properties ?? {}) };
      for (const field of shownFields) {
        updatedProperties[field.name] = coerceLocalValue(draft[field.name], field.type);
      }

      if (layer.sharedLayerId) {
        setSaving(true);
        setMessage(null);
        try {
          const updated = await layerLibraryService.updateSharedLayerFeature(
            ctx.module.id,
            layer.id,
            index,
            updatedProperties,
            // Attributes-only editors never send a geometry change — the
            // backend enforces this too (see modules_router.py's
            // update_shared_layer_feature), this is the honest client-side
            // half of the same rule, not just hiding the Reshape button.
            canEditGeometry ? workingGeometry : null,
          );
          const updatedFeatures = updated.localData?.features ?? [];
          ctx.onLayerChange?.(layer.id, {
            localData: updated.localData,
            bounds: updated.localData ? computeBounds(updated.localData) : undefined,
          });
          setRecords(updatedFeatures);
          setMessage({ tone: 'info', text: 'Saved — visible to everyone this layer is shared with.' });
          originalDraftRef.current = { ...updatedProperties };
          if (canEditGeometry) originalGeometryRef.current = workingGeometry ?? originalGeometryRef.current;
        } catch (error) {
          setMessage({
            tone: 'warn',
            text: error instanceof Error ? error.message : 'Could not save that change.',
          });
        } finally {
          setSaving(false);
        }
        return;
      }

      const updatedFeatures = features.slice();
      updatedFeatures[index] = {
        ...original,
        properties: updatedProperties,
        geometry: canEditGeometry ? (workingGeometry ?? original.geometry) : original.geometry,
      };
      const updatedData = { ...layer.localData, features: updatedFeatures };
      ctx.onLayerChange?.(layer.id, { localData: updatedData, bounds: computeBounds(updatedData) });
      setRecords(updatedFeatures);
      setMessage({ tone: 'info', text: 'Saved.' });
      originalDraftRef.current = { ...updatedProperties };
      if (canEditGeometry) originalGeometryRef.current = workingGeometry ?? originalGeometryRef.current;
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      const editServiceUrl = arcgisServiceUrlFor(layer, { arcgisUrl: ctx.arcgisUrl });
      const baseUrl = getArcgisBaseUrl(editServiceUrl);
      const attributes: Record<string, unknown> = {};
      for (const field of shownFields) attributes[field.name] = draft[field.name] ?? null;

      if (isNew) {
        const esriGeometry = toEsriGeometry(workingGeometry!);
        if (!esriGeometry) {
          setMessage({ tone: 'warn', text: 'This shape cannot be sent to the service.' });
          setSaving(false);
          return;
        }
        const body = new URLSearchParams({
          f: 'json',
          adds: JSON.stringify([{ geometry: esriGeometry, attributes }]),
        });
        const response = await fetch(
          withArcgisToken(`${baseUrl}/${layer.arcgisLayerId}/applyEdits`, editServiceUrl),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
          },
        );
        const data = response.ok ? await response.json() : null;
        const result = data?.addResults?.[0];

        if (result?.success) {
          const newFeature: GeoJSON.Feature = {
            type: 'Feature',
            properties: { ...attributes, [info.objectIdField]: result.objectId },
            geometry: workingGeometry!,
          };
          setRecords((current) => [...current, newFeature]);
          setMessage({ tone: 'info', text: 'Feature created.' });
          closeActive();
        } else {
          setMessage({
            tone: 'warn',
            text: result?.error?.description || data?.error?.message || 'The service refused to create this feature.',
          });
        }
        return;
      }

      attributes[info.objectIdField] = Number(activeId);
      // Same rule as the shared-layer branch above: an attributes-only
      // editor's request never carries a geometry key at all, not even an
      // unchanged one — nothing to trust the ArcGIS service to ignore.
      const esriGeometry = canEditGeometry && workingGeometry ? toEsriGeometry(workingGeometry) : null;
      const update: Record<string, unknown> = { attributes };
      if (esriGeometry) update.geometry = esriGeometry;

      const body = new URLSearchParams({
        f: 'json',
        updates: JSON.stringify([update]),
      });
      const response = await fetch(
        withArcgisToken(`${baseUrl}/${layer.arcgisLayerId}/applyEdits`, ctx.arcgisUrl),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString(),
        },
      );
      const data = response.ok ? await response.json() : null;
      const result = data?.updateResults?.[0];

      if (result?.success) {
        setMessage({ tone: 'info', text: 'Saved to the service.' });
        setRecords((current) =>
          current.map((feature) =>
            String((feature.properties as any)?.[info.objectIdField]) === activeId
              ? {
                  ...feature,
                  properties: { ...(feature.properties ?? {}), ...draft },
                  geometry: canEditGeometry ? (workingGeometry ?? feature.geometry) : feature.geometry,
                }
              : feature,
          ),
        );
        originalDraftRef.current = { ...draft };
        if (canEditGeometry) originalGeometryRef.current = workingGeometry ?? originalGeometryRef.current;
      } else {
        setMessage({
          tone: 'warn',
          text: result?.error?.description || data?.error?.message || 'The service refused the edit.',
        });
      }
    } catch {
      setMessage({ tone: 'warn', text: 'The service could not be reached.' });
    } finally {
      setSaving(false);
    }
  };

  /** Deletes the open record — the mirror of `save()`'s three branches
   *  (shared layer write-through, plain local layer, ArcGIS service). Never
   *  offered for a not-yet-saved sketch (NEW_FEATURE_ID) — that one is just
   *  discarded via Cancel, nothing to delete on either end yet. */
  const remove = async () => {
    if (!layer || !info || !activeId || activeId === NEW_FEATURE_ID) return;
    // Belt and suspenders, same reasoning as `save()`'s isNew guard — the
    // Delete button is already disabled without `canEditGeometry`.
    if (!canEditGeometry) return;
    if (!window.confirm('Delete this feature? This cannot be undone.')) return;

    if (layer.localData) {
      const index = Number(activeId);

      if (layer.sharedLayerId) {
        setSaving(true);
        setMessage(null);
        try {
          const updated = await layerLibraryService.deleteSharedLayerFeature(ctx.module.id, layer.id, index);
          const updatedFeatures = updated.localData?.features ?? [];
          ctx.onLayerChange?.(layer.id, {
            localData: updated.localData,
            bounds: updated.localData ? computeBounds(updated.localData) : undefined,
          });
          setRecords(updatedFeatures);
          setMessage({ tone: 'info', text: 'Deleted — removed for everyone this layer is shared with.' });
          closeActive();
        } catch (error) {
          setMessage({
            tone: 'warn',
            text: error instanceof Error ? error.message : 'Could not delete that feature.',
          });
        } finally {
          setSaving(false);
        }
        return;
      }

      const currentFeatures = layer.localData.features ?? [];
      const updatedFeatures = currentFeatures.filter((_, i) => i !== index);
      const updatedData = { ...layer.localData, features: updatedFeatures };
      ctx.onLayerChange?.(layer.id, { localData: updatedData, bounds: computeBounds(updatedData) });
      setRecords(updatedFeatures);
      setMessage({ tone: 'info', text: 'Deleted.' });
      closeActive();
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      const editServiceUrl = arcgisServiceUrlFor(layer, { arcgisUrl: ctx.arcgisUrl });
      const baseUrl = getArcgisBaseUrl(editServiceUrl);
      const body = new URLSearchParams({ f: 'json', deletes: activeId });
      const response = await fetch(
        withArcgisToken(`${baseUrl}/${layer.arcgisLayerId}/applyEdits`, editServiceUrl),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString(),
        },
      );
      const data = response.ok ? await response.json() : null;
      const result = data?.deleteResults?.[0];

      if (result?.success) {
        setRecords((current) =>
          current.filter(
            (feature) => String((feature.properties as any)?.[info.objectIdField]) !== activeId,
          ),
        );
        setMessage({ tone: 'info', text: 'Deleted from the service.' });
        closeActive();
      } else {
        setMessage({
          tone: 'warn',
          text: result?.error?.description || data?.error?.message || 'The service refused to delete this feature.',
        });
      }
    } catch {
      setMessage({ tone: 'warn', text: 'The service could not be reached.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ToolPanelHeader
        title="Edit"
        description="Add, reshape and edit features, and write them back."
        onClose={ctx.onClose}
      />

      {!canEditAttributes && (
        <Notice tone="warn">You do not have permission to edit data in this map.</Notice>
      )}

      <Field label="Layer">
        <LayerSelect layers={allowed} value={layerId} onChange={setLayerId} />
      </Field>

      {/* Session-wide, not per-action: applies to both sketching a new
          feature and dragging an existing one's vertices, so it does not
          need to be re-armed switching between the two mid-edit. */}
      <label className="flex cursor-pointer items-center justify-between gap-2 py-1">
        <span className="text-[12px] text-slate-600">Snap to nearby features</span>
        <input
          type="checkbox"
          checked={ctx.snapEnabled ?? false}
          onChange={(event) => ctx.onSnapEnabledChange?.(event.target.checked)}
          className="h-3.5 w-3.5 accent-blue-600"
        />
      </label>

      {loading && (
        <p className="flex items-center gap-2 text-[12px] text-slate-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Asking the service what it allows…
        </p>
      )}

      {!loading && info && !info.editable && <Notice tone="warn">{info.reason}</Notice>}

      {!loading && info?.editable && !activeId && !creatingShape && (
        <>
          {/* Geometry / Attribute — governs which of the two lists below
              shows, not two different feature sets: creating and reshaping
              are geometry work, browsing and searching by field is
              attribute work, but both operate on the same `records`. */}
          <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            {(['geometry', 'attribute'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                aria-pressed={activeSubTab === tab}
                onClick={() => setActiveSubTab(tab)}
                className={`flex-1 rounded-md py-1.5 text-[12px] font-medium capitalize transition ${
                  activeSubTab === tab
                    ? 'bg-white text-slate-800 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {activeSubTab === 'geometry' && (
            <>
              {layer && canEditGeometry && (
                <Field label="New feature" hint="Click a shape, then sketch it on the map.">
                  <div className="grid grid-cols-3 gap-1.5">
                    {creatableShapesFor(layer).map((shape) => {
                      const Icon = CREATE_SHAPE_ICONS[shape];
                      return (
                        <button
                          key={shape}
                          type="button"
                          onClick={() => startCreating(shape)}
                          className="flex flex-col items-center gap-1 rounded-lg border border-slate-200 bg-white py-2 text-[11px] font-medium text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
                        >
                          <Icon className="h-4 w-4" />
                          {shape.charAt(0).toUpperCase() + shape.slice(1)}
                        </button>
                      );
                    })}
                  </div>

                  {extraShapeToolsFor(layer).length > 0 && (
                    <>
                      <span className="mb-1 mt-2.5 block text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">
                        Shape
                      </span>
                      <div className="grid grid-cols-4 gap-1.5">
                        {extraShapeToolsFor(layer).map((shape) => {
                          const { icon: Icon, label } = EXTRA_SHAPE_TOOLS[shape];
                          return (
                            <button
                              key={shape}
                              type="button"
                              title={label}
                              aria-label={label}
                              onClick={() => startCreating(shape)}
                              className="flex flex-col items-center gap-1 rounded-lg border border-slate-200 bg-white py-2 text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
                            >
                              <Icon className="h-4 w-4" />
                              <span className="text-[9.5px]">{label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </Field>
              )}

              {records.length === 0 ? (
                <EmptyHint>This layer has no features to edit yet.</EmptyHint>
              ) : (
                <div className="space-y-1">
                  <ResultHeader label="Update existing geometry" count={records.length} />
                  <p className="text-[10.5px] leading-snug text-slate-400">
                    Pick a feature to reshape it, or click one directly on the map.
                  </p>
                  <ScrollList max={220}>
                    {records.map((feature, index) => {
                      const properties = (feature.properties ?? {}) as Record<string, unknown>;
                      const first = shownFields[0]?.name;
                      const label = rowLabel(index, properties);
                      return (
                        <ResultRow
                          key={index}
                          title={(first && String(properties[first] ?? '').trim()) || label}
                          detail={label}
                          onClick={() => openRecord(feature, index)}
                        />
                      );
                    })}
                  </ScrollList>
                </div>
              )}
            </>
          )}

          {activeSubTab === 'attribute' && (
            <div className="space-y-1">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  value={attributeSearch}
                  onChange={(event) => setAttributeSearch(event.target.value)}
                  placeholder="Search by ID or value…"
                  className={`${inputClass} pl-8`}
                />
              </div>
              {(() => {
                const needle = attributeSearch.trim().toLowerCase();
                const matches = records
                  .map((feature, index) => ({ feature, index }))
                  .filter(({ feature, index }) => {
                    if (!needle) return true;
                    const properties = (feature.properties ?? {}) as Record<string, unknown>;
                    if (rowLabel(index, properties).toLowerCase().includes(needle)) return true;
                    return Object.values(properties).some((value) =>
                      String(value ?? '').toLowerCase().includes(needle),
                    );
                  });
                if (matches.length === 0) {
                  return <EmptyHint>{records.length === 0 ? 'This layer has no features yet.' : 'No features match that search.'}</EmptyHint>;
                }
                return (
                  <ScrollList max={260}>
                    {matches.map(({ feature, index }) => {
                      const properties = (feature.properties ?? {}) as Record<string, unknown>;
                      const first = shownFields[0]?.name;
                      const label = rowLabel(index, properties);
                      return (
                        <ResultRow
                          key={index}
                          title={(first && String(properties[first] ?? '').trim()) || label}
                          detail={label}
                          onClick={() => openRecord(feature, index)}
                        />
                      );
                    })}
                  </ScrollList>
                );
              })()}
            </div>
          )}
        </>
      )}

      {!loading && info?.editable && creatingShape && (
        <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-[12px] leading-snug text-blue-700">
          {creatingShape === 'point'
            ? 'Click the map to place the point.'
            : creatingShape === 'circle'
              ? 'Click the centre, then click again to set the radius.'
              : creatingShape === 'rectangle'
                ? 'Click one corner, then click the opposite corner.'
                : creatingShape === 'freehand-polygon' || creatingShape === 'freehand-line'
                  ? 'Press and drag to trace the shape, then release to finish.'
                  : `Click each ${creatingShape === 'polygon' ? 'corner' : 'point'}. Finish with the button below, by pressing Enter, by clicking the first point again, or by double-clicking.`}
          {(ctx.sketchVertexCount ?? 0) > 0 && (
            <span className="mt-0.5 block text-[11px] text-blue-600/80">
              {ctx.sketchVertexCount} point{ctx.sketchVertexCount === 1 ? '' : 's'} placed · Esc abandons this shape
            </span>
          )}
          <ToolActions>
            {(creatingShape === 'line' || creatingShape === 'polygon') &&
              (ctx.sketchVertexCount ?? 0) >= (creatingShape === 'polygon' ? 3 : 2) && (
              <button
                type="button"
                onClick={() => ctx.onFinishFeatureSketch?.()}
                className={`${btnClass} gl-btn--primary`}
              >
                <Check className="h-3.5 w-3.5" />
                Finish shape
              </button>
            )}
            <button type="button" onClick={cancelCreating} className={`${btnClass} gl-btn--secondary`}>
              Cancel
            </button>
          </ToolActions>
        </div>
      )}

      {!loading && info?.editable && activeId && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <ResultHeader
              label={
                activeId === NEW_FEATURE_ID
                  ? 'New feature'
                  : info.objectIdField === LOCAL_ID_FIELD
                    ? `Row ${Number(activeId) + 1}`
                    : `${info.objectIdField} ${activeId}`
              }
              count={shownFields.length}
            />
            <button
              type="button"
              onClick={closeActive}
              className="text-[11px] font-semibold text-blue-600 hover:text-blue-800"
            >
              {activeId === NEW_FEATURE_ID ? 'Discard' : 'Back to list'}
            </button>
          </div>

          {canEditGeometry && workingGeometry && ['Point', 'LineString', 'Polygon'].includes(workingGeometry.type) && (
            <>
              <ToolActions>
                {!reshaping && !moving && ['LineString', 'Polygon'].includes(workingGeometry.type) && (
                  <button type="button" onClick={toggleReshape} className={`${btnClass} gl-btn--secondary`}>
                    <Move className="h-3.5 w-3.5" />
                    Reshape geometry
                  </button>
                )}
                {!reshaping && (
                  <button
                    type="button"
                    onClick={toggleMove}
                    className={`${btnClass} ${moving ? 'gl-btn--primary' : 'gl-btn--secondary'}`}
                  >
                    <Hand className="h-3.5 w-3.5" />
                    {moving ? 'Done moving' : 'Move feature'}
                  </button>
                )}
              </ToolActions>

              {!reshaping && !moving && (
                <ToolActions>
                  <button
                    type="button"
                    onClick={undoGeometry}
                    disabled={!canUndoGeometry}
                    title="Undo the last geometry change"
                    className={`${btnClass} gl-btn--secondary`}
                  >
                    <Undo2 className="h-3.5 w-3.5" />
                    Undo
                  </button>
                  <button
                    type="button"
                    onClick={redoGeometry}
                    disabled={!canRedoGeometry}
                    title="Redo the undone geometry change"
                    className={`${btnClass} gl-btn--secondary`}
                  >
                    <Redo2 className="h-3.5 w-3.5" />
                    Redo
                  </button>
                </ToolActions>
              )}

              {reshaping && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-[12px] leading-snug text-blue-700">
                  Sketch a new path across the part you want to change — the
                  span of the original shape between where your sketch
                  starts and ends will be replaced by it.
                  {(ctx.sketchVertexCount ?? 0) > 0 && (
                    <span className="mt-0.5 block text-[11px] text-blue-600/80">
                      {ctx.sketchVertexCount} point{ctx.sketchVertexCount === 1 ? '' : 's'} placed · Esc cancels
                    </span>
                  )}
                  <ToolActions>
                    {(ctx.sketchVertexCount ?? 0) >= 2 && (
                      <button
                        type="button"
                        onClick={() => ctx.onFinishFeatureSketch?.()}
                        className={`${btnClass} gl-btn--primary`}
                      >
                        <Check className="h-3.5 w-3.5" />
                        Finish sketch
                      </button>
                    )}
                    <button type="button" onClick={toggleReshape} className={`${btnClass} gl-btn--secondary`}>
                      Cancel
                    </button>
                  </ToolActions>
                </div>
              )}
              {moving && <Notice>Drag the feature anywhere on the map to move it.</Notice>}
            </>
          )}

          <ScrollList max={260}>
            {shownFields.map((field) => (
              <Field key={field.name} label={field.alias}>
                {field.choices ? (
                  <select
                    value={String(draft[field.name] ?? '')}
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, [field.name]: event.target.value }))
                    }
                    className={inputClass}
                  >
                    <option value="">—</option>
                    {field.choices.map((choice) => (
                      <option key={String(choice.code)} value={String(choice.code)}>
                        {choice.label}
                      </option>
                    ))}
                  </select>
                ) : field.type === 'boolean' ? (
                  <select
                    value={String(draft[field.name] ?? '')}
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, [field.name]: event.target.value === 'true' }))
                    }
                    className={inputClass}
                  >
                    <option value="">—</option>
                    <option value="true">True</option>
                    <option value="false">False</option>
                  </select>
                ) : (
                  <input
                    type={field.type === 'number' ? 'number' : 'text'}
                    value={String(draft[field.name] ?? '')}
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, [field.name]: event.target.value }))
                    }
                    className={inputClass}
                  />
                )}
              </Field>
            ))}
          </ScrollList>

          <ToolActions>
            <button
              type="button"
              onClick={() => void save()}
              disabled={saving || !canEditAttributes || !hasChanges}
              className={`${btnClass} gl-btn--primary`}
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {saving ? 'Saving…' : activeId === NEW_FEATURE_ID ? 'Create feature' : 'Save'}
            </button>
            <button type="button" onClick={closeActive} className={`${btnClass} gl-btn--secondary`}>
              Cancel
            </button>
            {activeId !== NEW_FEATURE_ID && (
              <button
                type="button"
                onClick={() => void remove()}
                disabled={saving || !canEditGeometry}
                className={`${btnClass} gl-btn--danger`}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </button>
            )}
          </ToolActions>

          {message && <Notice tone={message.tone}>{message.text}</Notice>}
        </div>
      )}
    </>
  );
}

/* ──────────────────────────────── Swipe ─────────────────────────────────── */

export function SwipePanel({ ctx }: { ctx: ToolContext }) {
  /*
   * MapView only knows how to clip layers that draw as ONE positioned image
   * with a box the swipe pane can clip: a GeoServer WMS tile, an ArcGIS
   * MapServer export image, and an imported GeoTIFF (drawn as an
   * `L.imageOverlay`, or the legacy per-tile georaster layer — either way,
   * one box). A FeatureServer layer, or a file-backed VECTOR layer
   * (`localData`), draws individual shapes with no single box to clip, so
   * picking one here would leave the divider on screen doing nothing when
   * dragged — the exact complaint this filter exists to head off, rather
   * than let someone find it by trying.
   */
  const allowed = scopedLayers(ctx.config, ctx.layers)
    .filter((layer) => layer.checked)
    .filter((layer) => {
      const sourceKind = detectLayerSource(layer, ctx.module);
      return sourceKind === 'wms' || sourceKind === 'arcgis-map' || sourceKind === 'local-raster';
    });
  const configuredDirection = cfg<string>(ctx.config, 'direction', 'horizontal');

  // `horizontal` in the Tool Library means the divider SLIDES horizontally,
  // which needs a vertical bar — the two words describe different halves of the
  // same thing, so the translation happens once, here.
  const vertical = ctx.swipeVertical ?? configuredDirection === 'vertical';
  const activeId = ctx.swipeLayerId ?? '';

  return (
    <>
      <ToolPanelHeader
        title="Swipe"
        description="Drag a divider to reveal what is underneath a layer."
        onClose={ctx.onClose}
      />

      {allowed.length === 0 ? (
        <EmptyHint>
          Switch on at least one layer in the Layers tab — swipe reveals what sits beneath it.
        </EmptyHint>
      ) : (
        <>
          <Field label="Swipe this layer">
            <LayerSelect
              layers={allowed}
              value={activeId}
              onChange={(id) => ctx.onSwipeChange?.({ layerId: id || null, vertical })}
              placeholder="Swipe is off"
            />
          </Field>

          <Field label="Divider">
            <select
              value={vertical ? 'vertical' : 'horizontal'}
              onChange={(event) =>
                ctx.onSwipeChange?.({
                  layerId: activeId || null,
                  vertical: event.target.value === 'vertical',
                })
              }
              className={inputClass}
            >
              <option value="horizontal">Slides left and right</option>
              <option value="vertical">Slides up and down</option>
            </select>
          </Field>

          {activeId ? (
            <ToolActions>
              <button
                type="button"
                onClick={() => ctx.onSwipeChange?.({ layerId: null, vertical })}
                className={`${btnClass} gl-btn--secondary`}
              >
                Turn swipe off
              </button>
            </ToolActions>
          ) : (
            <EmptyHint>Pick a layer to start. Drag the divider on the map to compare.</EmptyHint>
          )}
        </>
      )}
    </>
  );
}
